// 桌面 App 图标(原本嵌在 Desktop.tsx 里,M4 拆出独立组件以便:
//   1. 文件夹弹层 FolderPopover 也直接复用同一渲染
//   2. T-4.6.1 测试可单独 mount
//
// 拖拽:M5 接入。组件本身不知道"该派给哪个 helper",只通过 onDragStart /
// onDragEnd 事件把 payload + DragEvent 抛给 dispatcher;dispatcher 在
// Desktop.tsx / FolderPopover.tsx 中按 source(desktop / folder)分流。
import type { CSSProperties, DragEvent, MouseEvent } from 'react';

import ownedAppBadge from '../assets/owned-app-badge.webp';
import type { RegistryEntry } from '../apps/registryContext';
import { AppIcon } from './AppIcon';
import { DesktopIconLabel } from './DesktopIconLabel';
import {
  clearActiveDragPayload,
  serializePayload,
  type DragPayload,
} from './desktopDnD';

interface IconPos {
  row: number;
  col: number;
}

interface DesktopAppIconProps {
  entry: RegistryEntry;
  /** 已存的格子坐标(M4 之后由 layout 顺序流式排布;保留只为兼容旧调用方)。 */
  pos?: IconPos;
  /** 没有显式坐标时,按数组顺序作为 CSS `order` 渲染。 */
  fallbackIndex: number;
  // 单击启动(对齐 macOS Dock / Win 任务栏惯例)。键盘 Enter / Space 同效。
  onActivate: () => void;
  /** 拖动起源标签:'desktop' = 顶层 App;'folder' = 弹层内 App。 */
  dragSource?: 'desktop' | 'folder';
  /** 弹层内 App 必须带 folderId,以便 dispatcher 知道从哪个 folder 移出。 */
  folderId?: string;
  /** dispatcher:接收落点 + payload,自己决定调用哪个 helper。 */
  onDragEnd?: (event: DragEvent<HTMLButtonElement>, payload: DragPayload) => void;
  /** 右键:桌面上的 App 用来弹「移入新建文件夹」菜单;弹层内的成员不传。 */
  onContextMenu?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export function DesktopAppIcon({
  entry,
  pos,
  fallbackIndex,
  onActivate,
  dragSource,
  folderId,
  onDragEnd,
  onContextMenu,
}: DesktopAppIconProps) {
  const isExternal = Boolean(entry.externalUrl);
  const placementStyle: CSSProperties = pos
    ? { gridRow: pos.row + 1, gridColumn: pos.col + 1 }
    : { order: fallbackIndex };

  const draggable = Boolean(dragSource);
  const payload: DragPayload | null = !dragSource
    ? null
    : dragSource === 'desktop'
    ? { source: 'desktop', itemType: 'app', appId: entry.id }
    : folderId
    ? { source: 'folder', folderId, appId: entry.id }
    : null;

  return (
    <button
      type="button"
      role="gridcell"
      aria-label={entry.title}
      data-app-id={entry.id}
      data-desktop-kind={dragSource === 'desktop' ? 'app' : undefined}
      style={placementStyle}
      onClick={entry.disabled ? undefined : onActivate}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => {
        if (entry.disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate();
        }
      }}
      aria-disabled={entry.disabled || undefined}
      draggable={draggable}
      onDragStart={(e) => {
        if (payload) serializePayload(e.dataTransfer, payload);
      }}
      onDragEnd={(e) => {
        if (payload && onDragEnd) onDragEnd(e, payload);
        clearActiveDragPayload();
      }}
      title={entry.disabled ? '应用已下线' : isExternal ? '外部链接' : entry.title}
      className={`os-icon relative${entry.disabled ? ' os-icon-disabled' : ''}`}
    >
      <div className="os-icon-tile">
        <AppIcon icon={entry.icon} title={entry.title} size={48} appId={entry.id} />
        {entry.installed && !entry.marketInstalled ? (
          <img
            className="os-owned-app-tag"
            src={ownedAppBadge}
            alt="自有应用"
            title="自有应用"
            draggable={false}
          />
        ) : null}
      </div>
      <DesktopIconLabel text={entry.title} />
    </button>
  );
}
